// components/TentHouseService/tentpackage/TentPackagePage.js
import React, { useState, useEffect } from 'react';
import { tentPackageApi } from '../../../Apiservices/Auth/servers/tentPackageApi';
import TentPackageService from './TentPackageService';
import styles from './TentPackagePage.module.css';
import { fetchCategoryEvents } from '../../../Apiservices/Auth/servers/fetchCategoryEvents';

const TentPackagePage = () => {
  const [mode, setMode] = useState('view'); // 'view', 'create', 'edit'
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);

  // Load packages and events on component mount
  useEffect(() => {
    loadPackages();
    loadEvents();
  }, []);

  const loadPackages = async () => {
    setLoading(true);
    try {
      const response = await tentPackageApi.getPackages();
      setPackages(response.data || []);
    } catch (error) {
      console.error('Failed to load packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEvents = async () => {
    try {
      const eventsData = await fetchCategoryEvents();
      setEvents(eventsData || []);
    } catch (error) {
      console.error('Failed to load events:', error);
    }
  };

  // Function to get event IDs from recommendedEvents array
  const getEventIdsFromPackage = (pkg) => {
    if (!pkg.recommendedEvents || !Array.isArray(pkg.recommendedEvents)) {
      return [];
    }
    return pkg.recommendedEvents.map(event => event.id);
  };

  // Prepare package data for edit mode
  const preparePackageDataForEdit = (pkg) => {
    return {
      ...pkg,
      recommended_event_ids: getEventIdsFromPackage(pkg)
    };
  };

  const handleCreate = () => {
    setSelectedPackage(null);
    setMode('create');
  };

  const handleEdit = (pkg) => {
    setSelectedPackage(preparePackageDataForEdit(pkg));
    setMode('edit');
  };

  const handleView = () => {
    setMode('view');
    setSelectedPackage(null);
  };

  const handleSave = async (formData) => {
    try {
      const cleanedData = { ...formData };
      
      // Clean the copied object
      Object.keys(cleanedData).forEach(key => {
        if (typeof cleanedData[key] === 'string') {
          cleanedData[key] = cleanedData[key].trim() === '' ? null : cleanedData[key].trim();
        }
      });
      
      if (mode === 'create') {      
        await tentPackageApi.createPackage(cleanedData);
      } else if (mode === 'edit') {
        await tentPackageApi.updatePackage(selectedPackage.id, cleanedData);
      }
      
      // Reload packages and return to view mode
      await loadPackages();
      setMode('view');
      alert(`Package ${mode === 'create' ? 'created' : 'updated'} successfully!`);
    } catch (error) {
      console.error('Error saving package:', error);
      alert('Failed to save package. Please try again.');
      throw error;
    }
  };

  const handleDelete = async (pkgId) => {
    if (!window.confirm('Are you sure you want to delete this package?')) {
      return;
    }

    try {
      await tentPackageApi.deletePackage(pkgId);
      await loadPackages();
      alert('Package deleted successfully!');
    } catch (error) {
      console.error('Error deleting package:', error);
      alert('Failed to delete package. Please try again.');
    }
  };

  // Render event names
  const renderEventNames = (pkg) => {
    if (!pkg.recommendedEvents || pkg.recommendedEvents.length === 0) {
      return 'None';
    }
    
    return pkg.recommendedEvents
      .slice(0, 3) // Show only first 3 events
      .map(event => event.name)
      .join(', ') + (pkg.recommendedEvents.length > 3 ? '...' : '');
  };

  // Render based on current mode
  const renderContent = () => {
    switch (mode) {
      case 'create':
      case 'edit':
        return (
          <TentPackageService
            mode={mode}
            packageData={selectedPackage}
            onSave={handleSave}
            onCancel={handleView}
            events={events}
          />
        );
      
      case 'view':
      default:
        return (
          <div className={styles.viewMode}>
            <div className={styles.header}>
              <h1>Package Management</h1>
              <button 
                onClick={handleCreate}
                className={styles.createButton}
              >
                + Create New Package
              </button>
            </div>

            {loading ? (
              <div className={styles.loading}>Loading packages...</div>
            ) : packages.length === 0 ? (
              <div className={styles.noPackages}>
                <p>No packages found.</p>
                <button 
                  onClick={handleCreate}
                  className={styles.createButton} 
                >
                  Create Your First Package
                </button>
              </div>
            ) : (
              <div className={styles.packagesGrid}>
                {packages.map(pkg => (
                  <div key={pkg.id} className={styles.packageCard}>
                    <div className={styles.cardHeader}>
                      <h3>{pkg.package_name}</h3>
                      <div className={styles.cardActions}>
                        <button 
                          onClick={() => handleEdit(pkg)}
                          className={styles.editButton}
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => handleDelete(pkg.id)}
                          className={styles.deleteButton}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    
                    <div className={styles.cardContent}>
                      <p className={styles.description}>
                        {pkg.description || 'No description'}
                      </p>
                      
                      <div className={styles.details}>
                        <div className={styles.detailItem}>
                          <span className={styles.label}>Capacity:</span>
                          <span>{pkg.people_capacity} people</span>
                        </div>
                        <div className={styles.detailItem}>
                          <span className={styles.label}>Regular Price:</span>
                          <span>₹{pkg.regular_price}</span>
                        </div>
                        {pkg.offered_price && (
                          <div className={styles.detailItem}>
                            <span className={styles.label}>Offered Price:</span>
                            <span>₹{pkg.offered_price}</span>
                          </div>
                        )}
                        {pkg.offered_valid_till && (
                          <div className={styles.detailItem}>
                            <span className={styles.label}>Offer Valid Till:</span>
                            <span>{new Date(pkg.offered_valid_till).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>

                      <div className={styles.eventsSection}>
                        <h4>Recommended Events</h4>
                        <div className={styles.eventsList}>
                          {pkg.recommendedEvents && pkg.recommendedEvents.length > 0 ? (
                            <div className={styles.eventTags}>
                              {pkg.recommendedEvents.slice(0, 5).map(event => (
                                <span key={event.id} className={styles.eventTag}>
                                  {event.name}
                                </span>
                              ))}
                              {pkg.recommendedEvents.length > 5 && (
                                <span className={styles.moreEvents}>
                                  +{pkg.recommendedEvents.length - 5} more
                                </span>
                              )}
                            </div>
                          ) : (
                            <p className={styles.noEvents}>No recommended events</p>
                          )}
                        </div>
                      </div>

                      <div className={styles.itemsSection}>
                        <h4>Package Items ({pkg.package_items?.length || 0})</h4>
                        <div className={styles.itemsList}>
                          {pkg.package_items?.map((item, index) => (
                            <div key={index} className={styles.item}>
                              <span className={styles.itemName}>
                                {item.tent_house_service?.service_name || `Item ${index + 1}`}
                              </span>
                              <span className={styles.itemQty}>Qty: {item.item_qty}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <div className={styles.tentPackagePage}>
      {renderContent()}
    </div>
  );
};

export default TentPackagePage;